import { EarthAmericasIcon, ChevronDownIcon } from 'bda-web-ui-components';
import { useEffect, useRef, useState } from 'react';

type LangCode = 'es' | 'en';

export interface LanguageSelectProps {
  value: LangCode;
  onChange: (_value: LangCode) => void;
  className?: string;
}

const OPTIONS: Array<{ value: LangCode; label: string }> = [
  { value: 'es', label: 'ES' },
  { value: 'en', label: 'EN' },
];

export const LanguageSelect = ({
  value,
  onChange,
  className = '',
}: LanguageSelectProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  const current =
    OPTIONS.find(o => o.value === value)?.label ?? value.toUpperCase();

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger SIN borde/background */}
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1 uppercase font-semibold text-base text-primary focus:outline-none cursor-pointer"
      >
        <EarthAmericasIcon className="h-5 w-5 fill-primary shrink-0" />
        <span aria-label="current-language" className="text-xs">
          {current}
        </span>
        <ChevronDownIcon className={`h-3 w-3 fill-primary`} />
      </button>

      {/* Dropdown */}
      {open && (
        <ul
          role="listbox"
          tabIndex={-1}
          className="absolute mt-2 w-20 bg-white rounded-md shadow-lg ring-1 ring-black/5 py-1 px-1"
        >
          {OPTIONS.map(opt => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`px-3 py-2 cursor-pointer text-xs hover:bg-gray-50 ${
                opt.value === value
                  ? 'text-primary font-semibold'
                  : 'text-gray-900'
              }`}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
